import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  ChevronDown, MapPin, DollarSign, Clock, LayoutList,
  Calendar, GraduationCap, Lock, Aperture, Filter,
  RotateCcw, Loader2, Search, Brain, X
} from "lucide-react";
import { useTheme } from '../Home/ThemeContext';
import { getAvailableStates } from "../../services/filter";

const continentData = {
  "Asia": ["India", "China", "Japan", "Singapore", "Malaysia", "South Korea", "UAE", "Thailand"],
  "Europe": ["United Kingdom", "Germany", "France", "Italy", "Spain", "Netherlands", "Ireland", "Sweden", "Switzerland"],
  "North America": ["United States", "Canada", "Mexico"],
  "Oceania": ["Australia", "New Zealand"],
  "South America": ["Brazil", "Argentina", "Chile"],
  "Africa": ["South Africa", "Egypt"]
};

const disciplineData = [
  "Agriculture & Forestry", "Applied Sciences & Professions", "Arts, Design & Architecture",
  "Business & Management", "Computer Science & IT", "Education & Training",
  "Engineering & Technology", "Environmental Studies & Earth Sciences",
  "Hospitality, Leisure & Sports", "Humanities", "Journalism & Media", "Law",
  "Medicine & Health", "Natural Sciences & Mathematics", "Social Sciences",
];

const durationData = ["1 Year", "2 Years", "3 Years", "4 Years", "5 Years"];
const formatData = ["Full-time", "Part-time", "Online"];
const attendanceData = ["On Campus", "Online", "Hybrid"];
const degreeTypeData = ["Bachelor's Degree", "Master's Degree", "MBA", "M.Tech", "Ph.D.", "Diploma"];
const specialProgrammeData = ["Executive Programmes", "Joint Programmes"];

export const initialFilters = {
  disciplines: [], countries: [], states: [], continents: [],
  tuitionFee: { min: "", max: "" },
  durations: [], format: [], attendance: [], degreeTypes: [], specialProgrammes: [],
};

const cleanStateName = (name) => {
  if (!name) return "";
  return name.replace(/\s*\([^)]*\)/g, "").trim();
};

const Checkbox = ({ label, checked, onChange, count, isDarkMode }) => (
  <div
    onClick={(e) => { e.stopPropagation(); onChange(); }}
    className={`flex items-center justify-between gap-2 cursor-pointer p-2 rounded-lg transition-colors group select-none ${
      isDarkMode ? "hover:bg-slate-700" : "hover:bg-indigo-50"
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
        checked ? "bg-indigo-600 border-indigo-600" : isDarkMode ? "border-slate-600 group-hover:border-slate-400" : "border-slate-300 group-hover:border-indigo-400"
      }`}>
        {checked && (
          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      <span className={`text-sm ${isDarkMode ? "text-slate-300 group-hover:text-white" : "text-slate-700 group-hover:text-indigo-900"}`}>
        {label}
      </span>
    </div>
    {count !== undefined && count > 0 && (
      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${isDarkMode ? "bg-slate-700 text-slate-400" : "bg-slate-100 text-slate-500"}`}>
        {count.toLocaleString()}
      </span>
    )}
  </div>
);

export default function CollegeFilter({ currentFilters, onFilterChange, onClearAll, onApplyFilters }) {
  const context = useTheme();
  const isDarkMode = context?.isDarkMode || false;

  const [activeSection, setActiveSection] = useState(null);
  const [tempFilters, setTempFilters] = useState(currentFilters || initialFilters);
  const [disciplineSearch, setDisciplineSearch] = useState("");
  const [activeContinent, setActiveContinent] = useState(null);
  const [statesList, setStatesList] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);

  useEffect(() => {
    const fetchStates = async () => {
      setLoadingStates(true);
      try {
        const data = await getAvailableStates();
        if (data.success && data.states?.length) {
          const normalized = data.states
            .filter(s => s._id || s.Name)
            .map(s => ({
              Name: s._id || s.Name,
              DisplayName: cleanStateName(s._id || s.Name),
              Count: s.count || s.Count || 0,
            }));
          const seen = new Set();
          const deduped = [];
          for (const s of normalized) {
            const key = s.DisplayName.toLowerCase();
            if (!seen.has(key) && s.DisplayName) {
              seen.add(key);
              deduped.push(s);
            } else if (seen.has(key)) {
              const existing = deduped.find(d => d.DisplayName.toLowerCase() === key);
              if (existing) existing.Count += s.Count;
            }
          }
          deduped.sort((a, b) => a.DisplayName.localeCompare(b.DisplayName));
          setStatesList(deduped);
        }
      } catch (err) {
        console.error("Failed to load states:", err);
      } finally {
        setLoadingStates(false);
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (currentFilters) setTempFilters(currentFilters);
  }, [currentFilters]);

  // KEY FIX: Apply filters immediately when tempFilters change (after a short debounce)
  // This ensures single-click works. The parent just needs to call the API.
  const applyImmediately = useCallback((newFilters) => {
    onFilterChange(newFilters);
  }, [onFilterChange]);

  const toggleSection = (key) => setActiveSection(prev => prev === key ? null : key);

  const toggleArray = (category, value) => {
    setTempFilters(prev => {
      const list = prev[category] || [];
      const exists = list.includes(value);
      const newList = exists ? list.filter(i => i !== value) : [...list, value];
      const newStates = (category === "countries" && value === "India" && exists) ? [] : prev.states;
      const updated = { ...prev, [category]: newList, states: newStates };
      applyImmediately(updated);
      return updated;
    });
  };

  const toggleState = (stateName) => {
    setTempFilters(prev => {
      const list = prev.states || [];
      const exists = list.includes(stateName);
      const updated = { ...prev, states: exists ? list.filter(i => i !== stateName) : [...list, stateName] };
      applyImmediately(updated);
      return updated;
    });
  };

  const handleFeeChange = (field, value) => {
    setTempFilters(prev => ({ ...prev, tuitionFee: { ...prev.tuitionFee, [field]: value } }));
  };

  const handleApply = () => {
    onFilterChange(tempFilters);
    if (onApplyFilters) onApplyFilters();
  };

  const handleClearAll = () => {
    setTempFilters(initialFilters);
    setActiveSection(null);
    setActiveContinent(null);
    setDisciplineSearch("");
    if (onClearAll) onClearAll();
  };

  const handleResetSection = (key) => {
    setTempFilters(prev => {
      const updated = { ...prev };
      if (key === "tuitionFee") updated.tuitionFee = { min: "", max: "" };
      else if (key === "location") { updated.countries = []; updated.states = []; updated.continents = []; }
      else updated[key] = [];
      applyImmediately(updated);
      return updated;
    });
  };

  const activeCount = {
    disciplines: tempFilters.disciplines?.length || 0,
    location: (tempFilters.countries?.length || 0) + (tempFilters.states?.length || 0) + (tempFilters.continents?.length || 0),
    tuitionFee: (tempFilters.tuitionFee?.min || tempFilters.tuitionFee?.max) ? 1 : 0,
    durations: tempFilters.durations?.length || 0,
    format: tempFilters.format?.length || 0,
    attendance: tempFilters.attendance?.length || 0,
    degreeTypes: tempFilters.degreeTypes?.length || 0,
    specialProgrammes: tempFilters.specialProgrammes?.length || 0,
  };

  const totalActive = Object.values(activeCount).reduce((a, b) => a + b, 0);

  const filteredDisciplines = disciplineData.filter(d =>
    d.toLowerCase().includes(disciplineSearch.toLowerCase())
  );

  const indiaCount = useMemo(() =>
    statesList.reduce((acc, s) => acc + (s.Count || 0), 0), [statesList]
  );

  const t = {
    bg: isDarkMode ? "bg-slate-800" : "bg-white",
    border: isDarkMode ? "border-slate-700" : "border-slate-100",
    headerBg: isDarkMode ? "bg-slate-800" : "bg-white",
    headerText: isDarkMode ? "text-white" : "text-slate-900",
    sectionHover: isDarkMode ? "hover:bg-slate-700" : "hover:bg-slate-50",
    sectionText: isDarkMode ? "text-slate-200" : "text-slate-800",
    iconBg: isDarkMode ? "bg-slate-700 text-indigo-400" : "bg-indigo-50 text-indigo-600",
    input: isDarkMode
      ? "bg-slate-700 border-slate-600 text-white placeholder-slate-500 focus:border-indigo-500"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-indigo-500",
    footerBg: isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-100",
    activeBadge: "bg-indigo-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center",
  };

  const renderSection = (title, icon, key, children) => {
    const count = activeCount[key] || 0;
    return (
      <div className={`border-b pb-1 mb-1 ${t.border}`}>
        <div
          role="button" tabIndex={0}
          onClick={() => toggleSection(key)}
          onKeyDown={(e) => e.key === "Enter" && toggleSection(key)}
          className={`flex w-full justify-between items-center text-sm font-bold py-3 px-2 rounded-lg transition-colors cursor-pointer ${t.sectionHover}`}
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${t.iconBg}`}>{icon}</div>
            <span className={t.sectionText}>{title}</span>
            {count > 0 && <span className={t.activeBadge}>{count}</span>}
          </div>
          <ChevronDown className={`w-4 h-4 ${t.sectionText} transition-transform duration-300 ${activeSection === key ? "rotate-180" : ""}`} />
        </div>
        {activeSection === key && (
          <div className="mt-1 pl-1">
            <div className="flex justify-end mb-2 px-2">
              <button
                onClick={(e) => { e.stopPropagation(); handleResetSection(key); }}
                className="flex items-center gap-1 text-xs font-semibold text-indigo-500 hover:text-indigo-400"
              >
                <RotateCcw className="w-3 h-3" /> Reset
              </button>
            </div>
            <div className="space-y-0.5 max-h-72 overflow-y-auto pr-1 custom-scrollbar">
              {children}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`w-full h-full flex flex-col transition-colors duration-300 ${t.bg}`}>
      {/* HEADER */}
      <div className={`px-5 py-4 border-b flex-shrink-0 flex items-center justify-between ${t.headerBg} ${t.border}`}>
        <h2 className={`text-lg font-black flex items-center gap-2 ${t.headerText}`}>
          <Filter className="w-4 h-4 text-indigo-500" /> Filters
          {totalActive > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-indigo-600 text-white text-xs font-bold rounded-full">
              {totalActive}
            </span>
          )}
        </h2>
        <button
          onClick={handleClearAll}
          className="text-xs font-bold text-red-500 hover:text-red-400 bg-red-500/10 hover:bg-red-500/20 px-3 py-1.5 rounded-full transition-colors"
        >
          Clear All
        </button>
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto px-3 py-3 custom-scrollbar">

        {/* 1. DISCIPLINES */}
        {renderSection("Discipline / Course", <Aperture className="w-4 h-4" />, "disciplines",
          <>
            <div className="mb-2 px-1">
              <div className="relative">
                <Search className={`absolute left-3 top-2.5 w-3.5 h-3.5 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`} />
                <input
                  type="text" placeholder="Search discipline..." value={disciplineSearch}
                  onChange={e => setDisciplineSearch(e.target.value)}
                  className={`w-full pl-8 pr-3 py-2 text-xs rounded-lg border outline-none ${t.input}`}
                />
              </div>
            </div>
            {filteredDisciplines.length === 0 && (
              <p className={`text-xs px-2 py-2 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>No results</p>
            )}
            {filteredDisciplines.map(d => (
              <Checkbox key={d} label={d} checked={tempFilters.disciplines?.includes(d)}
                onChange={() => toggleArray("disciplines", d)} isDarkMode={isDarkMode} />
            ))}
          </>
        )}

        {/* 2. LOCATION */}
        {renderSection("Location", <MapPin className="w-4 h-4" />, "location",
          <div className="space-y-1">
            {Object.entries(continentData).map(([continent, countries]) => (
              <div key={continent}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <Checkbox label={continent} checked={tempFilters.continents?.includes(continent)}
                      onChange={() => toggleArray("continents", continent)} isDarkMode={isDarkMode} />
                  </div>
                  <div
                    role="button" tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setActiveContinent(p => p === continent ? null : continent); }}
                    onKeyDown={(e) => e.key === "Enter" && setActiveContinent(p => p === continent ? null : continent)}
                    className={`p-1.5 rounded-lg mr-1 cursor-pointer transition-colors ${isDarkMode ? "hover:bg-slate-700" : "hover:bg-slate-100"}`}
                  >
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeContinent === continent ? "rotate-180" : ""} ${isDarkMode ? "text-slate-400" : "text-slate-500"}`} />
                  </div>
                </div>
                {activeContinent === continent && (
                  <div className={`ml-5 pl-3 mt-1 border-l-2 ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}>
                    {countries.map(country => (
                      <div key={country}>
                        <Checkbox label={country}
                          count={country === "India" ? (indiaCount || undefined) : undefined}
                          checked={tempFilters.countries?.includes(country)}
                          onChange={() => toggleArray("countries", country)} isDarkMode={isDarkMode} />
                        {country === "India" && tempFilters.countries?.includes("India") && (
                          <div className={`ml-4 pl-2 mt-0.5 border-l-2 ${isDarkMode ? "border-slate-700" : "border-indigo-100"}`}>
                            {loadingStates ? (
                              <div className="flex items-center gap-2 text-xs text-slate-500 py-2 pl-2">
                                <Loader2 className="w-3 h-3 animate-spin text-indigo-500" /> Loading states...
                              </div>
                            ) : statesList.length === 0 ? (
                              <p className="text-xs text-slate-500 pl-2 py-1">No states found</p>
                            ) : (
                              statesList.map(state => (
                                <Checkbox key={state.Name} label={state.DisplayName}
                                  count={state.Count || undefined}
                                  checked={tempFilters.states?.includes(state.Name)}
                                  onChange={() => toggleState(state.Name)} isDarkMode={isDarkMode} />
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 3. TUITION FEE */}
        {renderSection("Tuition Fee", <DollarSign className="w-4 h-4" />, "tuitionFee",
          <div className="space-y-3 px-1 pb-1">
            <div>
              <label className={`text-xs font-semibold mb-1.5 block ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Min Fee (₹)</label>
              <input type="number" className={`w-full p-2.5 rounded-lg border text-sm outline-none transition-all ${t.input}`}
                value={tempFilters.tuitionFee?.min || ""} onChange={e => handleFeeChange("min", e.target.value)} placeholder="0" min="0" />
            </div>
            <div>
              <label className={`text-xs font-semibold mb-1.5 block ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Max Fee (₹)</label>
              <input type="number" className={`w-full p-2.5 rounded-lg border text-sm outline-none transition-all ${t.input}`}
                value={tempFilters.tuitionFee?.max || ""} onChange={e => handleFeeChange("max", e.target.value)} placeholder="50,00,000" min="0" />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[
                { label: "< ₹1L", min: "", max: "100000" },
                { label: "₹1–5L", min: "100000", max: "500000" },
                { label: "₹5–20L", min: "500000", max: "2000000" },
                { label: "> ₹20L", min: "2000000", max: "" },
              ].map(preset => {
                const isActive = tempFilters.tuitionFee?.min === preset.min && tempFilters.tuitionFee?.max === preset.max;
                return (
                  <button key={preset.label}
                    onClick={() => {
                      const updated = { ...tempFilters, tuitionFee: { min: preset.min, max: preset.max } };
                      setTempFilters(updated);
                      applyImmediately(updated);
                    }}
                    className={`text-xs px-2.5 py-1 rounded-full font-semibold border transition-all ${
                      isActive ? "bg-indigo-600 text-white border-indigo-600"
                        : isDarkMode ? "border-slate-600 text-slate-300 hover:border-indigo-500"
                        : "border-slate-300 text-slate-600 hover:border-indigo-400"
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* 4. DURATION */}
        {renderSection("Duration", <Clock className="w-4 h-4" />, "durations",
          durationData.map(d => (
            <Checkbox key={d} label={d} checked={tempFilters.durations?.includes(d)}
              onChange={() => toggleArray("durations", d)} isDarkMode={isDarkMode} />
          ))
        )}

        {/* 5. FORMAT */}
        {renderSection("Format", <LayoutList className="w-4 h-4" />, "format",
          formatData.map(f => (
            <Checkbox key={f} label={f} checked={tempFilters.format?.includes(f)}
              onChange={() => toggleArray("format", f)} isDarkMode={isDarkMode} />
          ))
        )}

        {/* 6. ATTENDANCE */}
        {renderSection("Attendance", <Calendar className="w-4 h-4" />, "attendance",
          attendanceData.map(a => (
            <Checkbox key={a} label={a} checked={tempFilters.attendance?.includes(a)}
              onChange={() => toggleArray("attendance", a)} isDarkMode={isDarkMode} />
          ))
        )}

        {/* 7. DEGREE TYPE */}
        {renderSection("Degree Type", <GraduationCap className="w-4 h-4" />, "degreeTypes",
          degreeTypeData.map(d => (
            <Checkbox key={d} label={d} checked={tempFilters.degreeTypes?.includes(d)}
              onChange={() => toggleArray("degreeTypes", d)} isDarkMode={isDarkMode} />
          ))
        )}

        {/* 8. SPECIAL PROGRAMMES */}
        {renderSection("Special Programmes", <Lock className="w-4 h-4" />, "specialProgrammes",
          specialProgrammeData.map(s => (
            <Checkbox key={s} label={s} checked={tempFilters.specialProgrammes?.includes(s)}
              onChange={() => toggleArray("specialProgrammes", s)} isDarkMode={isDarkMode} />
          ))
        )}

        {/* 9. PSYCHOMETRIC TEST */}
        <div className={`mt-3 p-4 rounded-2xl border ${isDarkMode ? "bg-indigo-900/20 border-indigo-800/50" : "bg-indigo-50 border-indigo-200"}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`p-1.5 rounded-lg ${isDarkMode ? "bg-indigo-900 text-indigo-400" : "bg-indigo-100 text-indigo-600"}`}>
              <Brain className="w-4 h-4" />
            </div>
            <span className={`text-sm font-bold ${isDarkMode ? "text-indigo-300" : "text-indigo-700"}`}>
              Not sure which course?
            </span>
          </div>
          <p className={`text-xs mb-3 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
            Take our Psychometric Test to discover your ideal career path.
          </p>
          <a href="/services/counselling#psychometric"
            className="block w-full text-center text-xs font-bold py-2.5 rounded-xl transition-all active:scale-95 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm shadow-indigo-500/30"
          >
            Take Psychometric Test →
          </a>
        </div>
      </div>

      {/* STICKY FOOTER */}
      <div className={`px-4 py-3 border-t flex-shrink-0 ${t.footerBg}`}>
        <button
          onClick={handleApply}
          className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white py-3 rounded-xl transition-all font-bold text-sm shadow-lg shadow-indigo-500/25 active:scale-[0.98]"
        >
          Apply Filters {totalActive > 0 && `(${totalActive})`}
        </button>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(99,102,241,0.2); border-radius: 20px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: rgba(99,102,241,0.5); }
      `}</style>
    </div>
  );
}